import {
  Activity,
  ArrowUpRight,
  Eye,
  Gift,
  History,
  RefreshCw,
  ShieldCheck,
  Ticket,
  Users,
  X,
} from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { toast } from "sonner";
import { api } from "../api";
import { loadLerunIssuer } from "../lerun-issuer";
import {
  ActionButton,
  EmptyState,
  ErrorState,
  LoadingState,
  Panel,
  Pagination,
  SectionHeader,
  Tag,
} from "../components/design-system";
import { useResource } from "../hooks/use-resource";
import { FilterPopover } from "../components/filter-popover";
import { AutoDormCheckGrantDialog } from "../components/auto-dorm-check-grant-dialog";
import { InlineQuotaValue } from "./auto-dorm-check";
import type {
  LerunCdk,
  LerunUser,
  LerunVisit,
  LerunSettings,
  LerunIssuer,
  LerunMetricCounts,
} from "../lerun-types";
import { formatDateTime } from "../utils";
import "./lerun.css";

type Workspace = "overview" | "cdks" | "visits" | "runs";
const labels: Record<string, string> = {
  available: "待发放",
  assigned: "已发放",
  redeemed: "已兑换",
  failed: "失败",
  revoked: "已作废",
  expired: "已过期",
  pending: "登录中",
  succeeded: "登录成功",
  completed: "成功乐跑",
  changed: "次数待核对",
};
function Status({ value }: { value: string }) {
  return (
    <Tag
      tone={
        value === "redeemed" || value === "succeeded"
          ? "sage"
          : value === "failed"
            ? "danger"
            : value === "assigned" || value === "pending"
              ? "clay"
              : "neutral"
      }
    >
      {labels[value] || value}
    </Tag>
  );
}
const date = (value: string | null) => (value ? formatDateTime(value) : "—");
const number = (value: number | undefined) =>
  value === undefined ? "—" : value.toLocaleString();

export function LerunPage() {
  const settings = useResource(() => api.lerunSettings());
  const issuerSequence = useRef(0);
  const issuer = useResource(async (): Promise<LerunIssuer> => {
    const sequence = ++issuerSequence.current;
    return loadLerunIssuer((cached) => {
      if (sequence === issuerSequence.current)
        issuer.setData((current) =>
          current?.snapshotAt &&
          cached.snapshotAt &&
          Date.parse(current.snapshotAt) > Date.parse(cached.snapshotAt)
            ? current
            : cached,
        );
    });
  });
  useEffect(
    () => () => {
      issuerSequence.current += 1;
    },
    [],
  );
  const [days, setDays] = useState(7);
  const overview = useResource(() => api.lerunOverview(days), [days]);
  const [workspace, setWorkspace] = useState<Workspace>("overview");
  const [saving, setSaving] = useState(false);
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const [revision, setRevision] = useState(0);
  const savingRef = useRef(false);
  const settingsRef = useRef(settings.data);
  settingsRef.current = settings.data;
  const rewardDirty = useRef(false);
  const [rewardSaveState, setRewardSaveState] = useState<
    "idle" | "pending" | "saving" | "saved" | "error"
  >("idle");
  const [rewardDraft, setRewardDraft] = useState({
    invitationReward: "2",
    checkinReward: "1",
  });
  const rewardDraftRef = useRef(rewardDraft);
  rewardDraftRef.current = rewardDraft;
  useEffect(() => {
    if (settings.data && !rewardDirty.current)
      setRewardDraft({
        invitationReward: String(settings.data.invitationReward),
        checkinReward: String(settings.data.checkinReward),
      });
  }, [settings.data?.invitationReward, settings.data?.checkinReward]);
  const saveSettings = async (patch: Partial<LerunSettings>, quiet = false) => {
    const current = settingsRef.current;
    if (!current || savingRef.current) return null;
    savingRef.current = true;
    setSaving(true);
    try {
      const next = await api.updateLerunSettings({
        entryEnabled: current.entryEnabled,
        functionEnabled: current.functionEnabled,
        freeMode: current.freeMode,
        invitationEnabled: current.invitationEnabled,
        checkinEnabled: current.checkinEnabled,
        invitationReward: current.invitationReward,
        checkinReward: current.checkinReward,
        version: current.version,
        ...patch,
      });
      settingsRef.current = next;
      settings.setData(next);
      if (!quiet) toast.success("设置已保存");
      return next;
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "保存失败，请重试");
      await settings.refresh();
      return null;
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };
  const rewardValues = (draft: typeof rewardDraft) => ({
    invitationReward: Number(draft.invitationReward),
    checkinReward: Number(draft.checkinReward),
  });
  const validRewards = (draft: typeof rewardDraft) =>
    Object.values(rewardValues(draft)).every(
      (value) => Number.isInteger(value) && value >= 1 && value <= 28,
    );
  const saveRewards = async () => {
    const draft = rewardDraftRef.current;
    if (!rewardDirty.current || savingRef.current || !validRewards(draft))
      return;
    const values = rewardValues(draft);
    if (
      values.invitationReward === settingsRef.current?.invitationReward &&
      values.checkinReward === settingsRef.current?.checkinReward
    ) {
      rewardDirty.current = false;
      setRewardSaveState("saved");
      return;
    }
    setRewardSaveState("saving");
    const next = await saveSettings(values, true);
    if (!next) setRewardSaveState("error");
    else if (rewardDraftRef.current === draft) {
      rewardDirty.current = false;
      setRewardSaveState("saved");
    } else setRewardSaveState("pending");
  };
  const editReward = (key: keyof typeof rewardDraft, value: string) => {
    rewardDirty.current = true;
    const draft = { ...rewardDraftRef.current, [key]: value };
    rewardDraftRef.current = draft;
    setRewardDraft(draft);
    setRewardSaveState("pending");
  };
  useEffect(() => {
    if (!rewardDirty.current || saving || rewardSaveState === "error") return;
    const timer = window.setTimeout(() => void saveRewards(), 500);
    return () => window.clearTimeout(timer);
  }, [rewardDraft, saving, settings.data?.version, rewardSaveState]);
  useEffect(() => {
    const retry = () => void saveRewards();
    window.addEventListener("online", retry);
    return () => window.removeEventListener("online", retry);
  }, []);
  const toggle = (
    key:
      | "entryEnabled"
      | "functionEnabled"
      | "freeMode"
      | "invitationEnabled"
      | "checkinEnabled",
  ) => saveSettings({ [key]: !settings.data?.[key] });
  const refresh = async () => {
    setRevision((value) => value + 1);
    await Promise.all([
      settings.refresh(),
      overview.refresh(),
      issuer.refresh(),
    ]);
  };
  const data = overview.data;
  return (
    <div className="lerun-page space-y-6">
      <SectionHeader
        eyebrow="CAMPUS SERVICES"
        title="小易乐跑"
        desc="OpenSZXD × 西南小易"
        action={
          <ActionButton
            busy={
              overview.refreshing || settings.refreshing || issuer.refreshing
            }
            onClick={() => void refresh()}
          >
            <RefreshCw size={14} />
            刷新
          </ActionButton>
        }
      />
      {settings.error && (
        <ErrorState
          error={settings.error}
          onRetry={() => void settings.refresh()}
        />
      )}
      <Panel>
        {!settings.data ? (
          <LoadingState rows={2} />
        ) : (
          <div className="lerun-controls">
            <Toggle
              title="入口显示"
              description="我的 · 校园互助"
              enabled={settings.data.entryEnabled}
              disabled={saving}
              icon={<Eye size={17} />}
              onChange={() => void toggle("entryEnabled")}
            />
            <Toggle
              title="服务启用"
              description="关闭后暂停访问与进行中的连接"
              enabled={settings.data.functionEnabled}
              disabled={saving}
              icon={<ShieldCheck size={17} />}
              onChange={() => void toggle("functionEnabled")}
            />
          </div>
        )}
      </Panel>
      {settings.data && (
        <Panel>
          <div className="lerun-controls lerun-reward-controls">
            <Toggle
              title="免费模式"
              description="用尽后自动补充 1 次，不限次数"
              enabled={settings.data.freeMode}
              disabled={saving}
              icon={<Ticket size={17} />}
              onChange={() => void toggle("freeMode")}
            />
            <Toggle
              title="邀请奖励"
              description="新用户登录后赠送，老用户只记录关系"
              enabled={settings.data.invitationEnabled}
              disabled={saving}
              icon={<Users size={17} />}
              onChange={() => void toggle("invitationEnabled")}
            />
            <Toggle
              title="签到奖励"
              description="每天一次，每周最多 3 天"
              enabled={settings.data.checkinEnabled}
              disabled={saving}
              icon={<Gift size={17} />}
              onChange={() => void toggle("checkinEnabled")}
            />
          </div>
          <div className="lerun-reward-settings">
            <label>
              每位新用户奖励
              <input
                type="number"
                min="1"
                max="28"
                step="1"
                required
                disabled={saving}
                value={rewardDraft.invitationReward}
                onChange={(event) =>
                  editReward("invitationReward", event.target.value)
                }
                onBlur={() => void saveRewards()}
              />
              <span>次</span>
            </label>
            <label>
              每次签到奖励
              <input
                type="number"
                min="1"
                max="28"
                step="1"
                required
                disabled={saving}
                value={rewardDraft.checkinReward}
                onChange={(event) =>
                  editReward("checkinReward", event.target.value)
                }
                onBlur={() => void saveRewards()}
              />
              <span>次</span>
            </label>
            <span
              className="lerun-reward-save-state"
              data-error={
                !validRewards(rewardDraft) || rewardSaveState === "error"
              }
              role="status"
            >
              {!validRewards(rewardDraft)
                ? "请输入 1–28 的整数"
                : rewardSaveState === "error"
                  ? "保存失败，请检查网络"
                  : rewardSaveState === "saving" ||
                      rewardSaveState === "pending"
                    ? "保存中…"
                    : rewardSaveState === "saved"
                      ? "已保存"
                      : "修改后自动保存"}
            </span>
          </div>
          <p className="lerun-reward-policy">
            每学期已有次数与累计赠送合计最多 28
            次，已使用的赠送仍占额度。关闭免费模式后，奖励在次数用尽时合并兑换；关闭的奖励方式不再赠送，已获得的奖励保留。
          </p>
        </Panel>
      )}
      <Panel>
        <div className="lerun-issuer">
          <div className="lerun-issuer-heading">
            <span className="lerun-issuer-icon">
              <Ticket size={22} />
            </span>
            <div>
              <h3>剩余 Key</h3>
              <p>
                {settings.data?.freeMode
                  ? "免费模式 · 用尽后补充 1 次"
                  : "奖励模式 · 用尽后合并兑换已获得次数"}
              </p>
            </div>
          </div>
          <div className="lerun-issuer-stock">
            <strong>
              {issuer.data?.remainingKeys == null
                ? "—"
                : number(issuer.data.remainingKeys)}
            </strong>
            <span>个单次 Key</span>
          </div>
          <div className="lerun-issuer-meta">
            <Tag
              tone={
                issuer.data?.syncStatus === "ok"
                  ? issuer.data.remainingKeys === 0
                    ? "danger"
                    : "sage"
                  : "neutral"
              }
            >
              {!issuer.data
                ? "读取中"
                : !issuer.data.configured
                  ? "待配置"
                  : issuer.data.syncStatus !== "ok"
                    ? "余额待更新"
                    : issuer.data.remainingKeys === 0
                      ? "余额不足"
                      : "自动兑换已接入"}
            </Tag>
            <small>
              {issuer.data?.snapshotAt
                ? `更新于 ${date(issuer.data.snapshotAt)}`
                : "暂无余额记录"}
            </small>
          </div>
        </div>
        {issuer.error && (
          <ErrorState
            error={issuer.error}
            onRetry={() => void issuer.refresh()}
          />
        )}
        {issuer.data?.syncStatus === "failed" && (
          <p className="lerun-issuer-warning">
            余额读取失败，当前显示上次结果，请刷新重试。
          </p>
        )}
        {issuer.data?.pending.length ? (
          <div className="lerun-issuer-pending">
            <h4>待完成的补充</h4>
            {issuer.data.pending.map((item) => (
              <button
                key={item.user.id}
                onClick={() => setSelectedUser(item.user.id)}
              >
                <span>
                  <strong>{item.user.name || item.user.account}</strong>
                  <small>{item.user.studentNumber || "—"}</small>
                </span>
                <Tag
                  tone={
                    item.status === "uncertain" || item.status === "generating"
                      ? "danger"
                      : "clay"
                  }
                >
                  {
                    {
                      generating: "生成待核对",
                      uncertain: "生成待核对",
                      generated: "等待兑换",
                      redeeming: "兑换待核对",
                    }[item.status]
                  }
                </Tag>
                <time>{date(item.updatedAt)}</time>
                <ArrowUpRight size={14} />
              </button>
            ))}
            <p>
              结果不明确的生成请求不会重复扣减余额，请联系 OpenSZXD
              核对；已生成的 Key 会在用户下次进入时继续兑换。
            </p>
          </div>
        ) : null}
      </Panel>
      <div className="lerun-workspace-bar">
        <nav aria-label="乐跑管理视图" className="lerun-tabs">
          {(
            [
              { id: "overview", label: "概览", icon: Activity },
              { id: "cdks", label: "CDK 管理", icon: Ticket },
              { id: "visits", label: "访问记录", icon: History },
              { id: "runs", label: "乐跑管理", icon: Activity },
            ] as const
          ).map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              aria-current={workspace === id ? "page" : undefined}
              onClick={() => setWorkspace(id)}
              className={workspace === id ? "is-active" : ""}
            >
              <Icon size={15} />
              {label}
            </button>
          ))}
        </nav>
        {workspace === "visits" && (
          <FilterPopover
            label="统计时间"
            hideLabel
            value={String(days)}
            onChange={(value) => setDays(Number(value))}
            options={[1, 7, 30, 90].map((day) => ({
              value: String(day),
              label: `近 ${day} 天`,
            }))}
          />
        )}
      </div>
      {workspace === "overview" && (
        <>
          {overview.error && (
            <ErrorState
              error={overview.error}
              onRetry={() => void overview.refresh()}
            />
          )}
          <div className="lerun-metrics">
            <Metric
              label="访问用户"
              values={data?.metrics?.visitUsers}
              unit="人"
              icon={<Users size={18} />}
            />
            <Metric
              label="登录成功"
              values={data?.metrics?.successfulLogins}
              unit="次"
              icon={<ShieldCheck size={18} />}
            />
            <Metric
              label="乐跑完成"
              values={data?.metrics?.completedRuns}
              unit="次"
              icon={<Activity size={18} />}
            />
            <Metric
              label="CDK 兑换"
              values={data?.metrics?.redeemedCdks}
              unit="张"
              icon={<Gift size={18} />}
              note={`累计兑换 ${number(data?.cdks.redeemedUnits)} 次乐跑`}
            />
          </div>
          <div className="lerun-overview-grid">
            <Panel>
              <div className="lerun-panel-heading">
                <h3>CDK 流转</h3>
                <Tag>累计</Tag>
              </div>
              <div className="lerun-cdk-flow">
                {(
                  [
                    { key: "available", label: "待发放" },
                    { key: "assigned", label: "已发放" },
                    { key: "redeemed", label: "已兑换" },
                  ] as const
                ).map(({ key, label }, index) => (
                  <div key={key}>
                    <span className="lerun-step">0{index + 1}</span>
                    <strong>{number(data?.cdks[key])}</strong>
                    <span>{label}</span>
                  </div>
                ))}
              </div>
              <div className="lerun-panel-footer">
                <span>
                  共 {number(data?.cdks.total)} 张 · 失败{" "}
                  {number(data?.cdks.failed)} 张
                </span>
                <button onClick={() => setWorkspace("cdks")}>
                  查看 CDK <ArrowUpRight size={14} />
                </button>
              </div>
              <div className="lerun-provider-note">
                <Ticket size={17} />
                <div>
                  <strong>
                    {settings.data?.cdkReady
                      ? "自动补充并兑换"
                      : "CDK 服务待配置"}
                  </strong>
                  <p>
                    有剩余次数时不兑换；次数用尽后按当前模式补充，Key
                    不展示给用户。
                  </p>
                </div>
              </div>
            </Panel>
            <Panel>
              <div className="lerun-panel-heading lerun-visitor-heading">
                <h3>访问用户</h3>
                <div className="flex items-center gap-3">
                  <FilterPopover
                    label="排行时间"
                    hideLabel
                    value={String(days)}
                    onChange={(value) => setDays(Number(value))}
                    options={[1, 7, 30, 90].map((day) => ({
                      value: String(day),
                      label: `近 ${day} 天`,
                    }))}
                  />
                  <button
                    className="lerun-text-link"
                    onClick={() => setWorkspace("visits")}
                  >
                    全部记录 <ArrowUpRight size={14} />
                  </button>
                </div>
              </div>
              {!data ? (
                <LoadingState rows={4} />
              ) : data.topUsers.length === 0 ? (
                <EmptyState
                  title="还没有访问记录"
                  description="用户进入小易乐跑后，记录会出现在这里。"
                />
              ) : (
                <div className="lerun-user-list">
                  {data.topUsers.map((item) => (
                    <button
                      key={item.user.id}
                      onClick={() => setSelectedUser(item.user.id)}
                    >
                      <span className="lerun-avatar">
                        {item.user.name.slice(0, 1)}
                      </span>
                      <span className="min-w-0 flex-1">
                        <strong>{item.user.name}</strong>
                        <small>{item.user.studentNumber || "—"}</small>
                      </span>
                      <span className="text-right">
                        <strong>{item.visits} 次</strong>
                        <small>{date(item.lastVisitAt)}</small>
                      </span>
                      <ArrowUpRight size={14} />
                    </button>
                  ))}
                </div>
              )}
            </Panel>
          </div>
        </>
      )}
      {workspace === "cdks" && (
        <CdkWorkspace revision={revision} onUser={setSelectedUser} />
      )}
      {workspace === "visits" && (
        <VisitWorkspace
          days={days}
          revision={revision}
          onUser={setSelectedUser}
        />
      )}
      {workspace === "runs" && <RunWorkspace revision={revision} />}
      {selectedUser && (
        <UserDialog
          key={selectedUser}
          id={selectedUser}
          onClose={() => setSelectedUser(null)}
        />
      )}
    </div>
  );
}

function Toggle({
  title,
  description,
  enabled,
  disabled,
  icon,
  onChange,
}: {
  title: string;
  description: string;
  enabled: boolean;
  disabled: boolean;
  icon: ReactNode;
  onChange: () => void;
}) {
  return (
    <article className="adc-control-item">
      <span
        className={`adc-control-icon ${enabled ? "is-positive" : "is-neutral"}`}
      >
        {icon}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <h3>{title}</h3>
          <span className={`adc-mini-state ${enabled ? "is-on" : ""}`}>
            {enabled ? "开启" : "关闭"}
          </span>
        </div>
        <p>{description}</p>
      </div>
      <button
        type="button"
        role="switch"
        aria-label={title}
        aria-checked={enabled}
        disabled={disabled}
        onClick={onChange}
        className={`adc-switch ${enabled ? "is-on" : ""}`}
      >
        <span />
      </button>
    </article>
  );
}
function Metric({
  label,
  values,
  unit,
  icon,
  note,
}: {
  label: string;
  values?: LerunMetricCounts;
  unit: string;
  icon: ReactNode;
  note?: string;
}) {
  return (
    <Panel>
      <div className="lerun-metric" role="group" aria-label={`${label}统计`}>
        <div className="lerun-metric-heading">
          <span>{label}</span>
          {icon}
        </div>
        <dl className="lerun-metric-values">
          <div className="lerun-metric-total">
            <dt>累计</dt>
            <dd>
              {number(values?.total)}
              <small>{unit}</small>
            </dd>
          </div>
          <div>
            <dt>近 7 天</dt>
            <dd>
              {number(values?.last7Days)}
              <small>{unit}</small>
            </dd>
          </div>
          <div>
            <dt>今日</dt>
            <dd>
              {number(values?.today)}
              <small>{unit}</small>
            </dd>
          </div>
        </dl>
        {note && <p className="lerun-metric-note">{note}</p>}
      </div>
    </Panel>
  );
}
function UserButton({
  user,
  onUser,
}: {
  user: LerunUser | null;
  onUser: (id: string) => void;
}) {
  return user ? (
    <button className="lerun-user-link" onClick={() => onUser(user.id)}>
      <strong>
        {user.name}
        <ArrowUpRight size={12} />
      </strong>
      <small>{user.studentNumber || "—"}</small>
    </button>
  ) : (
    <span className="text-ink-faint">—</span>
  );
}
function Filters({
  draft,
  onDraft,
  onSearch,
  status,
  onStatus,
  options,
  placeholder,
  optionLabels,
}: {
  draft: string;
  onDraft: (value: string) => void;
  onSearch: () => void;
  status: string;
  onStatus: (value: string) => void;
  options: string[];
  placeholder: string;
  optionLabels?: Record<string, string>;
}) {
  return (
    <div className="lerun-filters">
      <form
        onSubmit={(event) => {
          event.preventDefault();
          onSearch();
        }}
      >
        <input
          aria-label="搜索记录"
          value={draft}
          onChange={(event) => onDraft(event.target.value)}
          placeholder={placeholder}
          maxLength={100}
        />
        <ActionButton type="submit">搜索</ActionButton>
      </form>
      <FilterPopover
        label="记录状态"
        hideLabel
        value={status}
        onChange={onStatus}
        options={[
          { value: "all", label: "全部状态" },
          ...options.map((value) => ({
            value,
            label: optionLabels?.[value] || labels[value],
          })),
        ]}
      />
    </div>
  );
}
function CdkWorkspace({
  revision,
  onUser,
}: {
  revision: number;
  onUser: (id: string) => void;
}) {
  const [page, setPage] = useState(1),
    [draft, setDraft] = useState(""),
    [q, setQ] = useState(""),
    [status, setStatus] = useState("all");
  const [detail, setDetail] = useState<LerunCdk | null>(null);
  const resource = useResource(
    () => api.lerunCdks({ page, pageSize: 12, q, status }),
    [page, q, status, revision],
  );
  return (
    <Panel>
      <div className="lerun-panel-heading">
        <div>
          <h3>CDK 管理</h3>
          <p>查看发放对象与实际兑换用户</p>
        </div>
        <ActionButton disabled title="等待合作方 CDK 接口">
          <Gift size={14} />
          生成 CDK · 待接入
        </ActionButton>
      </div>
      <Filters
        draft={draft}
        onDraft={setDraft}
        onSearch={() => {
          setQ(draft.trim());
          setPage(1);
        }}
        status={status}
        onStatus={(value) => {
          setStatus(value);
          setPage(1);
        }}
        options={[
          "available",
          "assigned",
          "redeemed",
          "failed",
          "revoked",
          "expired",
        ]}
        placeholder="姓名、学号、批次或 CDK 尾号"
      />
      {resource.error && (
        <ErrorState
          error={resource.error}
          onRetry={() => void resource.refresh()}
        />
      )}
      {!resource.data ? (
        <LoadingState />
      ) : resource.data.items.length ? (
        <>
          <div className="lerun-table-scroll" aria-busy={resource.refreshing}>
            <table className="lerun-table">
              <thead>
                <tr>
                  <th>CDK / 批次</th>
                  <th>乐跑次数</th>
                  <th>状态</th>
                  <th>发放用户</th>
                  <th>兑换用户</th>
                  <th>兑换时间</th>
                  <th>
                    <span className="sr-only">操作</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {resource.data.items.map((item) => (
                  <tr key={item.id}>
                    <td>
                      <strong className="font-mono">{item.codeLabel}</strong>
                      <small>{item.batchName}</small>
                    </td>
                    <td>{item.quantity} 次</td>
                    <td>
                      <Status value={item.status} />
                    </td>
                    <td>
                      <UserButton user={item.assignedUser} onUser={onUser} />
                    </td>
                    <td>
                      <UserButton user={item.redeemedUser} onUser={onUser} />
                    </td>
                    <td>{date(item.redeemedAt)}</td>
                    <td>
                      <button
                        className="lerun-text-link"
                        onClick={() => setDetail(item)}
                      >
                        详情
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination
            className="p-5"
            page={resource.data.pagination.page}
            pages={resource.data.pagination.totalPages}
            total={resource.data.pagination.total}
            onChange={setPage}
          />
        </>
      ) : (
        <EmptyState
          title={q || status !== "all" ? "没有匹配的 CDK" : "还没有 CDK"}
          description={
            q || status !== "all"
              ? "调整搜索内容或状态筛选。"
              : "合作方接口接入后，即可生成并发放 CDK。"
          }
        />
      )}
      {detail && (
        <DetailDialog title="CDK 详情" onClose={() => setDetail(null)}>
          <div className="lerun-detail-code">
            <span>{detail.codeLabel}</span>
            <Status value={detail.status} />
          </div>
          <dl className="lerun-detail-grid">
            {[
              ["批次", detail.batchName],
              ["乐跑次数", `${detail.quantity} 次`],
              ["创建人", detail.createdBy],
              ["创建时间", date(detail.createdAt)],
              ["发放时间", date(detail.assignedAt)],
              ["兑换时间", date(detail.redeemedAt)],
              ["到期时间", date(detail.expiresAt)],
              ["兑换回执", detail.receiptId || "—"],
            ].map(([label, value]) => (
              <div key={label}>
                <dt>{label}</dt>
                <dd>{value}</dd>
              </div>
            ))}
          </dl>
          <div className="lerun-detail-grid">
            <div>
              <p>发放用户</p>
              <UserButton
                user={detail.assignedUser}
                onUser={(id) => {
                  setDetail(null);
                  onUser(id);
                }}
              />
            </div>
            <div>
              <p>兑换用户</p>
              <UserButton
                user={detail.redeemedUser}
                onUser={(id) => {
                  setDetail(null);
                  onUser(id);
                }}
              />
            </div>
          </div>
        </DetailDialog>
      )}
    </Panel>
  );
}
function VisitWorkspace({
  days,
  revision,
  onUser,
}: {
  days: number;
  revision: number;
  onUser: (id: string) => void;
}) {
  const [page, setPage] = useState(1),
    [draft, setDraft] = useState(""),
    [q, setQ] = useState(""),
    [status, setStatus] = useState("all");
  useEffect(() => setPage(1), [days]);
  const resource = useResource(
    () => api.lerunVisitUsers({ page, pageSize: 12, q, status, days }),
    [page, q, status, days, revision],
  );
  return (
    <Panel>
      <div className="lerun-panel-heading">
        <div>
          <h3>访问记录</h3>
          <p>每位用户的访问次数与登录结果</p>
        </div>
        <Tag>近 {days} 天</Tag>
      </div>
      <Filters
        draft={draft}
        onDraft={setDraft}
        onSearch={() => {
          setQ(draft.trim());
          setPage(1);
        }}
        status={status}
        onStatus={(value) => {
          setStatus(value);
          setPage(1);
        }}
        options={["pending", "succeeded", "failed"]}
        optionLabels={{
          pending: "有准备中的访问",
          succeeded: "有登录成功",
          failed: "有登录失败",
        }}
        placeholder="搜索姓名或学号"
      />
      {resource.error && (
        <ErrorState
          error={resource.error}
          onRetry={() => void resource.refresh()}
        />
      )}
      {!resource.data ? (
        <LoadingState />
      ) : resource.data.items.length ? (
        <>
          <div className="lerun-table-scroll" aria-busy={resource.refreshing}>
            <table className="lerun-table">
              <thead>
                <tr>
                  <th>用户</th>
                  <th>访问次数</th>
                  <th>登录成功</th>
                  <th>登录失败</th>
                  <th>最近访问</th>
                  <th>
                    <span className="sr-only">操作</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {resource.data.items.map((item) => (
                  <tr key={item.user.id}>
                    <td>
                      <UserButton user={item.user} onUser={onUser} />
                    </td>
                    <td>
                      <strong>{number(item.visits)} 次</strong>
                      {item.pendingLogins > 0 && (
                        <small>{number(item.pendingLogins)} 次准备中</small>
                      )}
                    </td>
                    <td>
                      <Tag tone="sage">{number(item.successfulLogins)} 次</Tag>
                    </td>
                    <td>
                      <Tag tone={item.failedLogins ? "clay" : "neutral"}>
                        {number(item.failedLogins)} 次
                      </Tag>
                    </td>
                    <td>{date(item.lastVisitAt)}</td>
                    <td>
                      <button
                        className="lerun-text-link"
                        onClick={() => onUser(item.user.id)}
                      >
                        访问明细
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination
            className="p-5"
            page={resource.data.pagination.page}
            pages={resource.data.pagination.totalPages}
            total={resource.data.pagination.total}
            onChange={setPage}
          />
        </>
      ) : (
        <EmptyState
          title="没有匹配的访问记录"
          description="尝试调整时间范围或搜索条件。"
        />
      )}
    </Panel>
  );
}
function RunWorkspace({ revision }: { revision: number }) {
  const [page, setPage] = useState(1);
  const [draft, setDraft] = useState("");
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [selected, setSelected] = useState<string | null>(null);
  const [grantOpen, setGrantOpen] = useState(false);
  const resource = useResource(
    () => api.lerunAccounts({ page, pageSize: 12, q, status }),
    [page, q, status, revision],
    15000,
  );
  return (
    <>
      <Panel>
        <div className="lerun-panel-heading">
          <div>
            <h3>乐跑管理</h3>
            <p>乐跑结束后核对余额，成功次数以完成回执为准。</p>
          </div>
          <ActionButton tone="dark" onClick={() => setGrantOpen(true)}>
            <Gift size={14} />
            发放次数
          </ActionButton>
        </div>
        <Filters
          draft={draft}
          onDraft={setDraft}
          onSearch={() => {
            setQ(draft.trim());
            setPage(1);
          }}
          status={status}
          onStatus={(value) => {
            setStatus(value);
            setPage(1);
          }}
          options={["completed", "changed"]}
          placeholder="搜索姓名或学号"
        />
        {resource.error && (
          <ErrorState
            error={resource.error}
            onRetry={() => void resource.refresh()}
          />
        )}
        {!resource.data ? (
          <LoadingState />
        ) : resource.data.items.length ? (
          <>
            <div className="lerun-table-scroll" aria-busy={resource.refreshing}>
              <table className="lerun-table">
                <thead>
                  <tr>
                    <th>用户</th>
                    <th>待兑换次数</th>
                    <th>OpenSZXD 剩余</th>
                    <th>成功乐跑</th>
                    <th>最近成功</th>
                    <th>余额更新</th>
                    <th>次数变化</th>
                    <th>
                      <span className="sr-only">操作</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {resource.data.items.map((item) => (
                    <tr key={item.user.id}>
                      <td>
                        <UserButton user={item.user} onUser={setSelected} />
                      </td>
                      <td>
                        <InlineQuotaValue
                          user={{
                            ...item.user,
                            studentNumber: item.user.studentNumber ?? null,
                          }}
                          kind="count"
                          value={item.pendingRewards}
                          maximum={100000}
                          unit="次"
                          onChanged={resource.refresh}
                          onSave={(target, reason, key) =>
                            api.adjustLerunQuota(
                              item.user.id,
                              target,
                              reason,
                              key,
                            )
                          }
                        />
                      </td>
                      <td>
                        <strong className="lerun-balance-value">
                          {item.remaining === null
                            ? "—"
                            : `${item.remaining} 次`}
                        </strong>
                        {item.pendingConsumed > 0 && (
                          <small>
                            另有 {item.pendingConsumed} 次完成待核对
                          </small>
                        )}
                      </td>
                      <td>
                        <Tag tone={item.completedRuns ? "sage" : "neutral"}>
                          {item.completedRuns} 次
                        </Tag>
                      </td>
                      <td>{date(item.lastCompletedAt)}</td>
                      <td>
                        {date(item.snapshotAt)}
                        {item.syncStatus !== "ok" && (
                          <small>
                            {item.syncStatus === "failed"
                              ? "更新失败，保留上次余额"
                              : "待用户进入后获取"}
                          </small>
                        )}
                      </td>
                      <td>
                        {item.unexplainedChanges ? (
                          <Tag tone="clay">
                            {item.unexplainedChanges} 条待核对
                          </Tag>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td>
                        <button
                          className="lerun-text-link"
                          onClick={() => setSelected(item.user.id)}
                        >
                          次数明细 <ArrowUpRight size={13} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              className="p-5"
              page={resource.data.pagination.page}
              pages={resource.data.pagination.totalPages}
              total={resource.data.pagination.total}
              onChange={setPage}
            />
          </>
        ) : (
          <EmptyState
            title="没有匹配的乐跑用户"
            description="用户进入后可查看余额，或尝试调整筛选条件。"
          />
        )}
      </Panel>
      <AutoDormCheckGrantDialog
        product="lerun"
        open={grantOpen}
        plans={[]}
        onClose={() => setGrantOpen(false)}
        onGranted={async () => {
          await resource.refresh();
        }}
      />
      {selected && (
        <RunDialog
          key={selected}
          id={selected}
          onClose={() => setSelected(null)}
        />
      )}
    </>
  );
}
const eventLabels = {
  admin_grant: "管理员发放",
  admin_adjust: "管理员修改",
  opening_balance: "首次获取余额",
  balance_increase: "余额增加 · 来源未知",
  unexplained_decrease: "次数减少 · 来源未知",
  run_completed: "成功乐跑",
  run_failed: "乐跑失败",
  run_process: "流程记录",
  cdk_assigned: "发放 CDK",
  cdk_redeemed: "兑换 CDK",
  reward_invitation: "邀请新用户奖励",
  reward_checkin: "签到奖励",
  balance_reconciled: "使用后次数核对",
  agreement_opened: "打开使用协议",
  agreement_checked: "勾选同意",
  agreement_unchecked: "取消勾选",
  agreement_accepted: "确认协议并进入",
};
const processLabels: Record<string, string> = {
  website_session_resumed: "重新进入后复用原任务",
  terminal_transport_closed: "结果已送达，连接结束",
  browser_feedback: "网页状态反馈",
  heartbeat_replied: "上游心跳已响应",
  upstream_connecting: "连接 OpenSZXD",
  upstream_connected: "上游连接成功",
  browser_connected: "网页已连接",
  browser_reconnected: "网页已续接",
  browser_namespace_connected: "消息通道就绪",
  browser_disconnected: "网页断线",
  run_request_received: "收到开始请求",
  duplicate_run_ignored: "已拦截重复启动",
  authorization_started: "核验登录与次数",
  authorization_completed: "核验通过",
  upstream_request_sent: "已发送上游请求",
  upstream_event_received: "收到上游事件",
  upstream_frame_received: "收到上游消息帧",
  browser_frame_delivered: "消息帧已转发到网页",
  browser_acknowledgement_forwarded: "网页回执已转发",
  browser_event_delivered: "已转发到网页",
  progress_display_queued: "等待登录状态显示",
  scan_event_inferred: "补齐遗漏扫码事件",
  late_qr_ignored: "已忽略迟到二维码",
  verification_submitted: "已提交验证码",
  run_result_received: "收到乐跑结果",
  terminal_duplicate_ignored: "已忽略重复结果",
  balance_check_started: "开始核对次数",
  balance_check_completed: "次数核对完成",
  balance_check_failed: "次数核对失败",
  proxy_request_failed: "请求失败",
  result_persistence_failed: "结果记录失败",
  session_rejected: "会话不可用",
  upstream_transport_error: "上游连接异常",
  upstream_disconnected: "上游已断开",
  relay_closed: "流程结束",
};
const frameLabels: Record<string, string> = {
  event: "事件已识别",
  "event-unparsed": "事件未识别",
  binary: "二进制数据",
  "binary-event-header": "二进制事件",
  engine: "连接消息",
  "socket-control": "通道控制",
  "other-namespace-event": "其他通道事件",
  unknown: "格式未识别",
};
const socketLabels: Record<string, string> = {
  createNew: "开始乐跑",
  createVM: "开始准备",
  VMcreated: "准备完成",
  powerOn: "正在启动",
  qrcodeNew: "收到二维码",
  loggedIn: "已扫码，等待登录",
  searched: "微信登录已确认",
  openMiniProgram: "正在打开乐跑",
  loginLerun: "正在登录乐跑",
  verificationRequired: "需要验证码",
  verificationResult: "提交验证码",
  requestCompleteNew: "乐跑完成回执",
  error: "上游错误",
  easyLerunError: "页面错误状态",
  unparsed: "未识别的消息帧",
  binary: "二进制消息",
};
function RunDialog({ id, onClose }: { id: string; onClose: () => void }) {
  const [page, setPage] = useState(1);
  const [category, setCategory] = useState<"credits" | "agreement" | "process">(
    "credits",
  );
  const resource = useResource(
    () =>
      api.lerunUserActivity(id, {
        page,
        pageSize: category === "process" ? 50 : 12,
        category,
      }),
    [id, page, category],
    5000,
  );
  const data = resource.data;
  return (
    <DetailDialog title="乐跑使用明细" onClose={onClose}>
      {resource.error && (
        <ErrorState
          error={resource.error}
          onRetry={() => void resource.refresh()}
        />
      )}
      {!data ? (
        <LoadingState />
      ) : (
        <>
          <div className="lerun-detail-user">
            <span className="lerun-avatar">
              {data.account.user.name.slice(0, 1)}
            </span>
            <div>
              <h3>{data.account.user.name}</h3>
              <p>{data.account.user.studentNumber || "—"}</p>
            </div>
            <Tag tone="sage">{data.account.completedRuns} 次成功</Tag>
          </div>
          <div className="lerun-balance-summary">
            <div>
              <span>最近确认剩余</span>
              <strong>
                {data.account.remaining === null ? "—" : data.account.remaining}
                <small>次</small>
              </strong>
            </div>
            <div>
              <span>余额更新时间</span>
              <p>待兑换次数 {data.account.pendingRewards} 次</p>
              <p>{date(data.account.snapshotAt)}</p>
              {data.account.pendingConsumed > 0 && (
                <p>
                  另有 {data.account.pendingConsumed} 次完成，
                  {data.account.syncStatus === "failed"
                    ? "次数未核对"
                    : "次数核对中"}
                </p>
              )}
              {data.account.syncStatus === "failed" && (
                <p>最近更新失败，保留上次余额</p>
              )}
            </div>
          </div>
          <div className="lerun-detail-tabs" role="group" aria-label="明细类型">
            {(
              [
                ["credits", "次数记录"],
                ["agreement", "协议记录"],
                ["process", "流程记录"],
              ] as const
            ).map(([value, label]) => (
              <button
                key={value}
                className={category === value ? "is-active" : ""}
                onClick={() => {
                  setCategory(value);
                  setPage(1);
                }}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="lerun-ledger-note">
            {category === "credits" ? (
              <>
                来源未知的变化仅能确认发生区间，可能来自官网使用、其他发放或上游调整。
              </>
            ) : category === "process" ? (
              <>保留 14 天，按时间倒序展示每一步的上游事件、转发结果和耗时。</>
            ) : (
              <>
                最近确认：{date(data.account.agreedAt)} ·{" "}
                {data.account.agreementVersion || "尚未确认"}
                。打开或勾选不等于确认进入。
              </>
            )}
          </div>
          {data.items.length ? (
            <div className="lerun-ledger" aria-busy={resource.refreshing}>
              {data.items.map((item) => (
                <article key={item.id}>
                  <span
                    className={`lerun-ledger-dot ${item.kind === "run_completed" || item.kind === "cdk_redeemed" ? "is-success" : ""}`}
                  />
                  <div className="lerun-ledger-content">
                    <div>
                      <strong>
                        {item.process
                          ? processLabels[item.process.event] ||
                            item.process.event
                          : eventLabels[item.kind]}
                      </strong>
                      <span className="lerun-ledger-delta">
                        {item.delta === null
                          ? item.kind === "cdk_assigned"
                            ? `${item.quantity} 次`
                            : ""
                          : `${item.delta > 0 ? "+" : ""}${item.delta} 次`}
                      </span>
                    </div>
                    <time>
                      {date(item.occurredAt)}
                      {item.intervalStart ? " 发现" : ""}
                    </time>
                    {item.process && (
                      <div className="lerun-process-details">
                        <p>
                          流程 {item.referenceId?.slice(0, 8)} · 第{" "}
                          {item.process.sequence} 步 · +
                          {(item.process.elapsedMs / 1000).toFixed(3)} 秒
                        </p>
                        {item.process.details.upstreamEvent && (
                          <p>
                            {socketLabels[
                              String(item.process.details.upstreamEvent)
                            ] || item.process.details.upstreamEvent}{" "}
                            <code>{item.process.details.upstreamEvent}</code>
                          </p>
                        )}
                        {typeof item.process.details.bytes === "number" && (
                          <p>
                            {frameLabels[String(item.process.details.format)] ||
                              "格式未识别"}
                            {" · "}
                            {item.process.details.bytes} 字节
                            {item.process.details.acknowledgement === true
                              ? " · 附回执编号"
                              : ""}
                          </p>
                        )}
                        {item.process.details.partnerMessage && (
                          <p className="lerun-process-error">
                            上游错误：{item.process.details.partnerMessage}
                          </p>
                        )}
                        {item.process.details.failedReason && (
                          <p className="lerun-process-error">
                            失败原因：{item.process.details.failedReason}
                          </p>
                        )}
                        {item.process.details.event && (
                          <p>
                            {item.process.details.event ===
                            "login_view_displayed"
                              ? "登录等待页已显示"
                              : `网页已收到：${socketLabels[String(item.process.details.event).replace("received:", "")] || item.process.details.event}`}
                          </p>
                        )}
                        <details>
                          <summary>事件详情</summary>
                          <pre>
                            {JSON.stringify(
                              {
                                runId: item.referenceId,
                                status: item.process.runStatus,
                                occurredAt: item.occurredAt,
                                ...item.process.details,
                              },
                              null,
                              2,
                            )}
                          </pre>
                        </details>
                      </div>
                    )}
                    {category === "agreement" && (
                      <p>协议版本：{item.referenceId}</p>
                    )}
                    {item.intervalStart && (
                      <p>
                        变化区间：{date(item.intervalStart)} 至{" "}
                        {date(item.occurredAt)}
                      </p>
                    )}
                    {item.balanceAfter !== null && (
                      <p>
                        {item.balanceBefore === null
                          ? "确认余额"
                          : `余额 ${item.balanceBefore} →`}{" "}
                        {item.balanceAfter} 次
                      </p>
                    )}
                    {item.createdBy && <p>操作人：{item.createdBy}</p>}
                    {item.reason && <p>{item.reason}</p>}
                    {item.kind === "unexplained_decrease" && (
                      <p>
                        差额未归属到单次乐跑，可能包含失败任务或其他入口操作。
                      </p>
                    )}
                    {item.kind === "balance_reconciled" && (
                      <p>
                        期间有本站成功记录；差额也可能包含免扣次数或其他入口操作。
                      </p>
                    )}
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <EmptyState
              title={
                category === "process"
                  ? "暂无流程记录"
                  : category === "credits"
                    ? "暂无次数明细"
                    : "暂无协议记录"
              }
              description={
                category === "process"
                  ? "流程明细保留 14 天。"
                  : category === "credits"
                    ? "尚未记录次数变化。"
                    : "尚未记录协议操作。"
              }
            />
          )}
          <Pagination
            page={data.pagination.page}
            pages={data.pagination.totalPages}
            total={data.pagination.total}
            onChange={setPage}
          />
        </>
      )}
    </DetailDialog>
  );
}
function VisitTable({
  items,
  onUser,
}: {
  items: LerunVisit[];
  onUser?: (id: string) => void;
}) {
  return (
    <div className="lerun-table-scroll">
      <table className="lerun-table">
        <thead>
          <tr>
            {onUser && <th>用户</th>}
            <th>访问时间</th>
            <th>设备</th>
            <th>登录状态</th>
            <th>乐跑发起 / 完成</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={item.id}>
              {onUser && (
                <td>
                  <UserButton user={item.user} onUser={onUser} />
                </td>
              )}
              <td>{date(item.createdAt)}</td>
              <td>
                {{
                  phone: "手机",
                  tablet: "平板",
                  desktop: "电脑",
                  unknown: "未知",
                }[item.device] || "未知"}
              </td>
              <td>
                <Status value={item.status} />
              </td>
              <td>
                {item.requestedRuns} / {item.completedRuns}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
function UserDialog({ id, onClose }: { id: string; onClose: () => void }) {
  const resource = useResource(() => api.lerunUser(id), [id]);
  const data = resource.data;
  return (
    <DetailDialog title="用户使用记录" onClose={onClose}>
      {resource.error && (
        <ErrorState
          error={resource.error}
          onRetry={() => void resource.refresh()}
        />
      )}
      {!data ? (
        <LoadingState />
      ) : (
        <>
          <div className="lerun-detail-user">
            <span className="lerun-avatar">{data.user.name.slice(0, 1)}</span>
            <div>
              <h3>{data.user.name}</h3>
              <p>{data.user.studentNumber || "—"}</p>
            </div>
            <Tag>{data.totalVisits} 次访问</Tag>
          </div>
          <p className="text-xs text-ink-faint">
            最近访问 {date(data.lastVisitAt)}
          </p>
          <h4 className="lerun-detail-heading">
            关联 CDK <span>最近 20 条</span>
          </h4>
          {data.cdks.length ? (
            <div className="lerun-table-scroll">
              <table className="lerun-table">
                <thead>
                  <tr>
                    <th>CDK / 批次</th>
                    <th>状态</th>
                    <th>关联方式</th>
                    <th>兑换时间</th>
                  </tr>
                </thead>
                <tbody>
                  {data.cdks.map((item) => (
                    <tr key={item.id}>
                      <td>
                        {item.codeLabel}
                        <small>{item.batchName}</small>
                      </td>
                      <td>
                        <Status value={item.status} />
                      </td>
                      <td>
                        {[
                          item.assignedUser?.id === id && "发放对象",
                          item.redeemedUser?.id === id && "兑换用户",
                        ]
                          .filter(Boolean)
                          .join("、")}
                      </td>
                      <td>{date(item.redeemedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="lerun-empty-inline">暂无关联 CDK</p>
          )}
          <h4 className="lerun-detail-heading">
            访问记录 <span>最近 20 条</span>
          </h4>
          {data.visits.length ? (
            <VisitTable items={data.visits} />
          ) : (
            <p className="lerun-empty-inline">暂无访问记录</p>
          )}
        </>
      )}
    </DetailDialog>
  );
}
function DetailDialog({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const dialog = ref.current;
    dialog?.showModal();
    return () => dialog?.close();
  }, []);
  return (
    <dialog
      ref={ref}
      className="lerun-dialog"
      aria-label={title}
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onClick={(event) => {
        if (event.target === ref.current) onClose();
      }}
    >
      <header>
        <h2>{title}</h2>
        <ActionButton aria-label="关闭详情" tone="ghost" onClick={onClose}>
          <X size={18} />
        </ActionButton>
      </header>
      <div className="lerun-dialog-body scrollbar-thin">{children}</div>
    </dialog>
  );
}
